// https://wiki.jtiweb.co.uk/display/DIGITECH/Price+format

import { getConfig, getLocale, leftToRightMark } from "@jti/fe-common";

type CurrencyDisplay = "symbol" | "code";

export type ParsedCurrencyFormat =
  | {
      displayCurrency: true;
      currencyPosition: "before" | "after";
      currencyDisplay: CurrencyDisplay;
      currencySeparator: string;
    }
  | {
      displayCurrency: false;
    };

type ParsedValueFormat = {
  decimalSeparator: string;
  decimalDigits: number;
  groupingSeparator: string;
};

type ParsedFormat = {
  currency: ParsedCurrencyFormat;
  value: ParsedValueFormat;
};

export type PriceType = {
  locale?: string;
  value: number;
  currency: string;
  currencyDisplay?: "symbol" | "code";
  format?: string;
  decimalDigitsOverride?: number;
};

const normalizeDecimalDigitsOverride = (decimalDigitsOverride?: number): number | undefined => {
  if (decimalDigitsOverride == null || !Number.isFinite(decimalDigitsOverride)) {
    return undefined;
  }
  const maxDecimalDigits = 10;
  const normalizedDecimalDigits = Math.trunc(decimalDigitsOverride);
  return Math.min(maxDecimalDigits, Math.max(0, normalizedDecimalDigits));
};

const parseCurrencyFormat = (format: string, valueFormat: string): ParsedCurrencyFormat => {
  const displayCurrency = valueFormat.length !== format.length;

  if (!displayCurrency) {
    return { displayCurrency: false };
  }

  const currencyPosition = format.startsWith("#") ? "after" : "before";
  const currency = format.replace(valueFormat, "");
  const currencySeparator = currency.replace(currency.trim(), "");
  const nonBreakingCurrencySeparator = currencySeparator.replace(/\s+/g, " ");
  const currencyDisplay = currency.replace(currencySeparator, "").length === 1 ? "symbol" : "code";

  return {
    displayCurrency,
    currencyPosition,
    currencyDisplay,
    currencySeparator: nonBreakingCurrencySeparator,
  };
};

const parseValueFormat = (valueFormat: string): ParsedValueFormat => {
  const separators = valueFormat.replaceAll("#", "").split("");
  const hasGroupingSeparator = valueFormat[1] !== "#";
  const groupingSeparator = hasGroupingSeparator ? valueFormat[1] : "";
  const hasDecimalPart = hasGroupingSeparator ? separators.length === 2 : separators.length === 1;
  const decimalSeparator = (hasDecimalPart && separators.pop()) || "";
  const decimalPart = (hasDecimalPart && valueFormat.split(decimalSeparator).pop()) || "";
  const decimalDigits = decimalPart.length;

  return {
    decimalSeparator,
    decimalDigits,
    groupingSeparator,
  };
};

export const parseFormat = (format: string): ParsedFormat => {
  const formatPattern =
    "^" +
    "(C{1}(C{2})? ?)" + // optional currency: exactly 1 or 3 currency symbols followed by an optional space
    "?#[^#]?###" + // integer part of the value: 1 hash followed by optional thousands separator followed by 3 hashes
    "([^#]#{1,3})?" + // optional decimal part of the value: decimal separator followed by 1 to 3 hashes
    "( ?C{1}(C{2})?)?" + // optional currency: an optional space followed by exactly 1 or 3 currency symbols
    "$";

  const formatRegex = new RegExp(formatPattern, "i");
  if (!formatRegex.test(format)) {
    throw new Error("Invalid price format");
  }

  const valueRegex = /#.*#/;
  const valueMatches = valueRegex.exec(format);
  if (valueMatches === null || valueMatches[0].length < 4) {
    throw new Error("Price format is incorrect");
  }

  const [valueFormat] = valueMatches;
  return {
    value: parseValueFormat(valueFormat),
    currency: parseCurrencyFormat(format, valueFormat),
  };
};

export const getCurrencySymbol = ({
  locale,
  currency,
  currencyDisplay = "symbol",
}: {
  locale: string;
  currency: string;
  currencyDisplay: CurrencyDisplay;
}): string => {
  const config = getConfig();
  // TODO: remove config?.[currency as keyof typeof config] once AEM is out of scope and currencies are migrated to customCurrencySymbol
  const configCurrencySymbol = config?.[currency as keyof typeof config] ?? config?.customCurrencySymbol?.[currency];

  if (configCurrencySymbol) {
    return `${configCurrencySymbol}${leftToRightMark}`;
  }

  const parts = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay,
  }).formatToParts(0);

  const currencySymbolPart = parts.find(({ type }) => type === "currency");
  const currencySymbol = currencySymbolPart?.value ? `${currencySymbolPart?.value}${leftToRightMark}` : "";

  return currencySymbol;
};

export const formatCurrency = ({
  locale,
  currency,
  currencyFormat,
}: {
  locale: string;
  currency: string;
  currencyFormat: ParsedCurrencyFormat;
}): string => {
  if (!currencyFormat.displayCurrency) {
    return "";
  }

  const { currencyPosition, currencyDisplay, currencySeparator } = currencyFormat;
  const currencySymbol = getCurrencySymbol({ locale, currency, currencyDisplay });
  return currencyPosition === "before"
    ? `${currencySymbol}${currencySeparator}`
    : `${currencySeparator}${currencySymbol}`;
};

const formatValue = ({ value, valueFormat }: { value: number; valueFormat: ParsedValueFormat }): string => {
  const { decimalSeparator, decimalDigits, groupingSeparator } = valueFormat;
  const fixedValue = value.toFixed(decimalDigits);
  const [integerPart, decimalPart] = decimalDigits > 0 ? fixedValue.split(".") : [fixedValue, ""];

  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupingSeparator);
  if (decimalDigits === 0) {
    return formattedIntegerPart;
  }

  return formattedIntegerPart + decimalSeparator + decimalPart;
};

export const formatWithIntl = ({
  locale = getLocale(),
  value,
  currency,
  currencyDisplay = "symbol",
  decimalDigitsOverride,
}: PriceType): string => {
  try {
    const normalizedDecimalDigitsOverride = normalizeDecimalDigitsOverride(decimalDigitsOverride);
    const fractionDigitsOptions =
      normalizedDecimalDigitsOverride == null
        ? {}
        : {
            minimumFractionDigits: normalizedDecimalDigitsOverride,
            maximumFractionDigits: normalizedDecimalDigitsOverride,
          };
    const config = getConfig();
    // TODO: remove config?.[currency as keyof typeof config] once AEM is out of scope and currencies are migrated to customCurrencySymbol
    const configCurrencySymbol = config?.[currency as keyof typeof config] ?? config?.customCurrencySymbol?.[currency];

    if (configCurrencySymbol && currencyDisplay === "symbol") {
      const formattedNumberParts = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        currencyDisplay: "code",
        ...fractionDigitsOptions,
      }).formatToParts(value);
      return formattedNumberParts.reduce((acc, { type, value }) => {
        if (type === "currency") {
          return `${acc}${configCurrencySymbol}${leftToRightMark}`;
        }
        return `${acc}${value}`;
      }, "");
    }

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay,
      ...fractionDigitsOptions,
    }).format(value);
  } catch (error) {
    console.error(error);
    // eslint-disable-next-line no-irregular-whitespace
    return `${currency}${leftToRightMark} ${value}${leftToRightMark}`;
  }
};

export const formatPrice = (price: PriceType): string => {
  const config = getConfig();
  const customFormat = price.format ?? config?.currencyFormat;
  const normalizedDecimalDigitsOverride = normalizeDecimalDigitsOverride(price.decimalDigitsOverride);

  if (!customFormat) {
    return formatWithIntl(price);
  }

  try {
    const { locale = getLocale(), value, currency } = price;
    const parsedFormat = parseFormat(customFormat);
    const valueFormat =
      normalizedDecimalDigitsOverride == null
        ? parsedFormat.value
        : { ...parsedFormat.value, decimalDigits: normalizedDecimalDigitsOverride };
    const formattedValue = formatValue({ value, valueFormat });

    if (!parsedFormat.currency.displayCurrency) {
      return formattedValue;
    }
    const formattedCurrency = formatCurrency({ locale, currency, currencyFormat: parsedFormat.currency });
    return parsedFormat.currency.currencyPosition === "before"
      ? `${formattedCurrency}${formattedValue}`
      : `${formattedValue}${formattedCurrency}`;
  } catch (error) {
    console.error(error);
    return formatWithIntl(price);
  }
};
